import { Alert, Button, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  storageKey, useStorageAction, type PolicyIn, type StorageAdmin, type StoragePolicy,
} from '@/api/storage-admin';
import { useUsers } from '@/api/users';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatBytes, type FieldValues } from '@/components/fields';
import { LocalDataTable } from '@/components/grid/LocalDataTable';
import { FieldsDialog, serverError } from './common';
import { policyFields, ROLE_OPTIONS } from './configFields';

const NEW_POLICY: FieldValues = { scope_type: 'ROLE', role: 'EMPLOYEE', allowed_extensions: [] };
const orDefault = (v: number | null) => (v == null ? 'Default' : formatBytes(v));

function toForm(p: StoragePolicy): FieldValues {
  return {
    ...p,
    role: p.scope_type === 'ROLE' ? p.scope_value : null,
    user_id: p.scope_type === 'USER' ? p.scope_value : null,
    allowed_extensions: p.allowed_extensions ?? [],
  };
}

function toBody(v: FieldValues): PolicyIn {
  const extensions = v.allowed_extensions as string[];
  return {
    scope_type: v.scope_type as PolicyIn['scope_type'],
    scope_value: String(v.scope_type === 'ROLE' ? v.role : v.user_id),
    quota_bytes: (v.quota_bytes as number | null) ?? null,
    max_file_bytes: (v.max_file_bytes as number | null) ?? null,
    allowed_extensions: extensions.length ? extensions : null,
    can_upload: (v.can_upload as boolean | null) ?? null,
    note: (v.note as string | null) || null,
  };
}

/** Exceptions to the workspace's rules for a role or one person. */
export function PoliciesSection({ admin }: { admin: StorageAdmin }) {
  const policies = useQuery({ queryKey: storageKey(admin, 'policies'), queryFn: () => admin.listPolicies!() });
  const users = useUsers();
  const [editing, setEditing] = useState<StoragePolicy | null | undefined>();
  const [deleting, setDeleting] = useState<StoragePolicy | null>(null);
  const [error, setError] = useState<string>();
  const save = useStorageAction(admin, ({ id, body }: { id: string | null; body: PolicyIn }) => admin.savePolicy!(id, body));
  const remove = useStorageAction(admin, (id: string) => admin.deletePolicy!(id));

  const userOptions = useMemo(() => (users.data ?? []).map((u) => ({ value: u.id, label: `${u.full_name} (${u.email})` })),
    [users.data]);
  const fields = useMemo(() => policyFields(userOptions), [userOptions]);
  const initial = useMemo(() => (editing ? toForm(editing) : NEW_POLICY), [editing]);
  const nameOf = (p: StoragePolicy) => (p.scope_type === 'ROLE'
    ? `Role · ${ROLE_OPTIONS.find((o) => o.value === p.scope_value)?.label ?? p.scope_value}`
    : `Person · ${userOptions.find((o) => o.value === p.scope_value)?.label ?? p.scope_value}`);

  const columns: GridColDef<StoragePolicy>[] = [
    { field: 'scope_value', headerName: 'Applies to', minWidth: 240, flex: 1, valueGetter: (_v, row) => nameOf(row) },
    { field: 'quota_bytes', headerName: 'Storage', minWidth: 110, valueFormatter: orDefault },
    { field: 'max_file_bytes', headerName: 'Largest file', minWidth: 110, valueFormatter: orDefault },
    {
      field: 'allowed_extensions', headerName: 'File types', minWidth: 160, flex: 1,
      valueFormatter: (v: string[] | null) => (v?.length ? v.join(', ') : 'Module lists'),
    },
    {
      field: 'can_upload', headerName: 'Uploads', minWidth: 110,
      valueFormatter: (v: boolean | null) => (v == null ? 'Default' : v ? 'Allowed' : 'Not allowed'),
    },
    { field: 'note', headerName: 'Note', minWidth: 160, flex: 1 },
    {
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: ({ row }) => (
        <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleting(row); }}>Delete</Button>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      {(policies.error || error) && (
        <Alert severity="error" onClose={() => setError(undefined)}>
          {error ?? serverError(policies.error).message}
        </Alert>
      )}
      <LocalDataTable
        rows={policies.data ?? []} columns={columns} getRowId={(r) => r.id} loading={policies.isLoading}
        searchText={(r) => `${nameOf(r)} ${r.note ?? ''}`} searchPlaceholder="Search policies"
        noun={['policy', 'policies']} onRowClick={(r) => setEditing(r)}
        emptyMessage="No exceptions — everyone follows the workspace’s quotas and module rules."
        toolbarEnd={<Button variant="contained" size="small" onClick={() => setEditing(null)}>Add policy</Button>}
      />
      <FieldsDialog
        open={editing !== undefined} title={editing ? `Edit ${nameOf(editing)}` : 'Add a policy'}
        fields={fields} initial={initial} onClose={() => setEditing(undefined)}
        fieldFor={(f, v) => (f === 'scope_value' ? (v.scope_type === 'ROLE' ? 'role' : 'user_id') : f)}
        onSubmit={(v) => save.mutateAsync({ id: editing?.id ?? null, body: toBody(v) })}
      />
      <ConfirmDialog
        open={!!deleting} destructive title="Delete this policy?" confirmLabel="Delete" busy={remove.isPending}
        description={deleting && <>The people it covers go back to the workspace’s rules: <b>{nameOf(deleting)}</b>.</>}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutateAsync(deleting.id)
          .catch((e) => setError(serverError(e).message))
          .finally(() => setDeleting(null))}
      />
    </Stack>
  );
}
